import { ActivityExerciseCard, Line } from '@components';
import { routes } from '@constants';
import { useNavigateWithOfflineMode, useScreenSize } from '@hooks';
import { activitiesSlice, activityApi, testApi, userApi } from '@store';
import { Button, Flex, Image, theme, Typography } from '@styles';
import { func } from 'prop-types';
import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
// import ClipLoader from 'react-spinners/ClipLoader';

const AdditionalExercisesList = ({ onLoginButtonClick }) => {
  const { i18n } = useTranslation();
  const dispatch = useDispatch();
  const { navigate } = useNavigateWithOfflineMode();
  const { isMobile, isTabletLG, isTabletSM } = useScreenSize();

  const jwtAuthenticationToken = useSelector(
    (state) => state.authSlice.jwtAuthenticationToken
  );
  const additionalExercisesState = useSelector(
    (state) => state.activitiesSlice.additionalExercisesState
  );

  const { refetch: refetchScenarios, scenarios } =
    activityApi.useGetScenariosQuery(undefined, {
      selectFromResult: ({ data }) => ({
        scenarios: data?.data?.scenarios,
      }),
    });
  const { refetch: refetchTests, tests } = testApi.useGetTestsQuery(undefined, {
    selectFromResult: ({ data }) => ({
      tests: data?.data?.tests,
    }),
  });
  const { data } = userApi.useGetUserQuery();

  useEffect(() => {
    refetchScenarios();
    refetchTests();
  }, [i18n.language]);

  const exercises = {
    scenarios,
    tests,
  };
  const availableExercises = exercises[additionalExercisesState]?.filter(
    (item) => item?.is_available
  );
  const unavailableExercises = exercises[additionalExercisesState]?.filter(
    (item) => !item?.is_available
  );

  const handleScenariosButtonClick = () => {
    dispatch(activitiesSlice.actions.setAdditionalExercisesState('scenarios'));
  };

  const handleTestsButtonClick = () => {
    dispatch(activitiesSlice.actions.setAdditionalExercisesState('tests'));
  };

  const handleLoginButtonPress = () => {
    onLoginButtonClick();
  };

  const handleNavigateCourses = () => {
    navigate(routes.courses);
  };

  const availableExercisesCards = useMemo(
    () =>
      availableExercises?.map((exercise) => (
        <ActivityExerciseCard
          key={exercise.id}
          id={exercise.id}
          imageUrl={exercise.image}
          isDisabled={false}
          isNew={exercise.to_notify}
          onPress={() => {
            navigate(`${routes.activities}/${additionalExercisesState}`, {
              state: { exerciseId: exercise.id },
            });
          }}
          titleText={exercise.title}
          wasSeenCount={exercise.views}
        />
      )),
    [availableExercises, additionalExercisesState]
  );

  const unavailableExercisesCards = useMemo(
    () =>
      unavailableExercises?.map((exercise) => (
        <ActivityExerciseCard
          key={exercise.id}
          id={exercise.id}
          imageUrl={exercise.image}
          isDisabled
          titleText={exercise.title}
          wasSeenCount={exercise.views}
        />
      )),
    [unavailableExercises, additionalExercisesState]
  );

  return jwtAuthenticationToken ? (
    <Flex flexDirection="column" mb={74} ml={isTabletLG ? 0 : 48} width="100%">
      <Flex justifyContent="space-between" mb={24}>
        <Flex gap={16}>
          <Button
            height={45}
            onClick={handleScenariosButtonClick}
            px={24}
            py={10}
            variant={
              additionalExercisesState === 'scenarios' ? 'primary' : 'secondary'
            }
          >
            <Typography
              color={
                additionalExercisesState === 'scenarios'
                  ? theme.colors.black[100]
                  : theme.colors.white[100]
              }
              variant="MS-bold-s16-lh25"
            >
              {i18n.t('Activities.Scenarios').toUpperCase()}
            </Typography>
          </Button>
          <Button
            height={45}
            onClick={handleTestsButtonClick}
            px={24}
            py={10}
            variant={
              additionalExercisesState === 'tests' ? 'primary' : 'secondary'
            }
          >
            <Typography
              color={
                additionalExercisesState === 'tests'
                  ? theme.colors.black[100]
                  : theme.colors.white[100]
              }
              variant="MS-bold-s16-lh25"
            >
              {i18n.t('Activities.Tests').toUpperCase()}
            </Typography>
          </Button>
        </Flex>
        {data?.data.rank?.image_large && (
          <Image
            alt="Rank"
            height={45}
            src={
              isMobile
                ? data?.data.rank?.image_small
                : data?.data.rank?.image_large
            }
            width={isMobile ? 45 : 112}
          />
        )}
      </Flex>
      {/* <ClipLoader color="#FFF504" size="60px" speedMultiplier={0.7} /> */}
      <Flex flexDirection="column" gap={3} mb={24} width="100%">
        {availableExercisesCards}
      </Flex>
      {availableExercises?.length === 0 && (
        <Flex
          alignItems="center"
          backgroundColor={theme.colors.white[10]}
          flexDirection={isTabletSM ? 'column' : 'row'}
          gap={16}
          justifyContent="space-between"
          mb={24}
          p={24}
        >
          <Typography
            color={theme.colors.white[60]}
            variant="MS-regular-s24-lh37"
            width={isTabletSM ? '100%' : '60%'}
          >
            {i18n.t('Activities.LockedActivities')}
          </Typography>
          <Button onClick={handleNavigateCourses} px={80} variant="primary">
            <Typography
              color={theme.colors.background}
              variant="MS-semibold-s16-lh25"
            >
              {i18n.t('Activities.Start').toUpperCase()}
            </Typography>
          </Button>
        </Flex>
      )}
      {unavailableExercises?.length > 0 && (
        <Flex alignItems="center" mb={24} width="100%">
          <Typography
            color={theme.colors.white[50]}
            mr={24}
            variant="BN-regular-s36-lh43"
          >
            {i18n.t('Activities.Locked').toUpperCase()}
          </Typography>
          <Line
            lineColor={theme.colors.white[50]}
            lineHeight={1}
            pb={2}
            width="100%"
          />
        </Flex>
      )}
      <Flex flexDirection="column" gap={3}>
        {unavailableExercisesCards}
      </Flex>
    </Flex>
  ) : (
    <Flex
      alignItems={isTabletLG ? 'center' : 'flex-end'}
      flexDirection="column"
      mb={80}
      ml={isTabletLG ? 0 : 48}
      width="100%"
    >
      <Flex
        alignItems="center"
        backgroundColor={theme.colors.hunterGreen}
        border={`1px solid ${theme.colors.whiteLine[100]}`}
        flexDirection="column"
        mb={24}
        ml={48}
        mr={isTabletLG ? 48 : 0}
        px={isTabletSM ? 16 : 0}
        py={isTabletLG ? 76 : 96}
        width="100%"
      >
        <Typography
          color={theme.colors.white[100]}
          mb={3}
          textAlign="center"
          variant={isTabletSM ? 'BN-bold-s64-lh64' : 'BN-bold-s88-lh75'}
        >
          {i18n.t('Activities.AccessProhibited')}
        </Typography>
        <Typography
          color={theme.colors.white[50]}
          maxWidth={374}
          textAlign="center"
          variant="MS-regular-s24-lh37"
          width="100%"
        >
          {i18n.t('Activities.AdditionalExercises')}
        </Typography>
      </Flex>
      <Button
        maxWidth={295}
        onClick={handleLoginButtonPress}
        py={22}
        variant="primary"
        width="100%"
      >
        <Typography
          color={theme.colors.black[100]}
          variant="MS-semibold-s16-lh25"
        >
          {i18n.t('AuthForm.LogIn').toUpperCase()}
        </Typography>
      </Button>
    </Flex>
  );
};

AdditionalExercisesList.propTypes = {
  onLoginButtonClick: func.isRequired,
};

export default AdditionalExercisesList;
